/**
 * コレクション無限スクロールと在庫切れ商品のソート機能
 */

class CollectionInfiniteScroll {
  constructor() {
    this.container = document.querySelector('[data-products-grid]');
    this.collectionSection = document.querySelector('[data-section-type="collection"]');
    
    if (!this.container || !this.collectionSection) {
      console.log('CollectionInfiniteScroll: コンテナまたはセクションが見つかりません');
      return;
    }
    
    this.collectionUrl = this.collectionSection.dataset.collection;
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.isSorting = false; // ソート中フラグ
    this.isInitialized = false; // 初期化完了フラグ
    this.debug = true; // デバッグモード（パフォーマンス調査中）
    
    // URLから現在のページ番号を取得
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('page')) {
      this.currentPage = parseInt(urlParams.get('page'), 10) || 1;
    }
    
    if (this.debug) {
      console.log('CollectionInfiniteScroll: 初期化', {
        collectionUrl: this.collectionUrl,
        currentPage: this.currentPage,
        initialProducts: this.container.querySelectorAll('[data-product-grid-item]').length
      });
    }
    
    this.init();
  }
  
  log(...args) {
    if (this.debug) {
      console.log('[CollectionInfiniteScroll]', ...args);
    }
  }
  
  init() {
    const startTime = performance.now();
    this.log('[初期化] 開始');
    
    // ページネーションを非表示
    const pagination = this.container.querySelector('.pagination-custom');
    if (pagination) {
      pagination.style.display = 'none';
    }
    
    // 無限スクロールイベントを即座に設定
    this.setupInfiniteScroll();
    this.log('[初期化] 無限スクロール設定完了');
    
    // フィルター/ソート変更時のリセット
    this.setupFilterSortListeners();
    this.log('[初期化] フィルター/ソートリスナー設定完了');
    
    // DOM変更の監視を設定
    this.setupMutationObserver();
    this.log('[初期化] MutationObserver設定完了');
    
    // 初期化完了（無限スクロールを有効化）
    this.isInitialized = true;
    this.log('[初期化] 無限スクロール有効化');
    
    // 初期ページの商品をソート（非同期で実行、ページ表示を優先）
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.log('[ソート] 開始（requestIdleCallback）');
        this.sortProducts();
      }, { timeout: 500 }); // 500ms以内には必ず実行
    } else {
      setTimeout(() => {
        this.log('[ソート] 開始（setTimeout fallback）');
        this.sortProducts();
      }, 10);
    }
    
    const endTime = performance.now();
    this.log(`[初期化] 完了（${(endTime - startTime).toFixed(2)}ms）`);
  }
  
  /**
   * フィルター/ソート変更時のリスナーを設定
   */
  setupFilterSortListeners() {
    // ソート変更を検知
    const sortSelect = document.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        // ページがAjaxで更新されるのを待つ
      });
    }
  }
  
  /**
   * DOM変更の監視を設定（フィルター/ソート実行後の再初期化用）
   */
  setupMutationObserver() {
    // 商品コンテナの親要素を監視
    const productsContainer = this.container.closest('[data-products-container]') || 
                             this.container.closest('.collection__products') ||
                             this.container.parentElement;
    
    if (!productsContainer) {
      this.log('商品コンテナが見つかりません');
      return;
    }
    
    // 処理中フラグ（短時間の連続呼び出しを防ぐ）
    let isProcessing = false;
    let processingTimeout = null;
    
    // MutationObserverで商品グリッドの変更を監視
    this.observer = new MutationObserver((mutations) => {
      // 初期化中、ソート中、または処理中は無視（無限ループ防止）
      if (!this.isInitialized || this.isSorting || isProcessing) {
        return;
      }
      
      mutations.forEach((mutation) => {
        // 商品グリッドが更新された場合（フィルター/ソート実行時）
        if (mutation.type === 'childList' && mutation.target === this.container) {
          // sentinelやloaderの追加/削除は無視
          const hasProductChanges = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && node.hasAttribute('data-product-grid-item')
          ) || Array.from(mutation.removedNodes).some(node => 
            node.nodeType === 1 && node.hasAttribute('data-product-grid-item')
          );
          
          if (!hasProductChanges) {
            return;
          }
          
          // 処理中フラグをセット
          isProcessing = true;
          
          // 既存のタイムアウトをクリア
          if (processingTimeout) {
            clearTimeout(processingTimeout);
          }
          
          // 少し遅延させてから再初期化
          processingTimeout = setTimeout(() => {
            // 初期化フラグをリセット（再初期化中はMutationObserverを無視）
            this.isInitialized = false;
            
            this.reset();
            this.sortProducts();
            
            // ページネーションを再度非表示
            const pagination = this.container.querySelector('.pagination-custom');
            if (pagination) {
              pagination.style.display = 'none';
            }
            
            // 既存のObserverとハンドラーをクリーンアップ
            if (this.intersectionObserver) {
              this.intersectionObserver.disconnect();
            }
            if (this.scrollHandler) {
              window.removeEventListener('scroll', this.scrollHandler);
            }
            
            // 無限スクロールを再設定
            this.setupInfiniteScroll();
            
            // 再初期化完了
            this.isInitialized = true;
            
            // lazySizesを更新
            if (window.lazySizes) {
              const lazyImages = this.container.querySelectorAll('.lazyload:not(.lazyloaded)');
              lazyImages.forEach(img => {
                window.lazySizes.loader.unveil(img);
              });
            }
            
            // 処理完了後、一定時間後にフラグをリセット
            setTimeout(() => {
              isProcessing = false;
            }, 300);
          }, 100);
        }
      });
    });
    
    // 監視を開始
    this.observer.observe(this.container, {
      childList: true,
      subtree: false
    });
  }
  
  /**
   * 無限スクロールをリセット
   */
  reset() {
    this.currentPage = 1;
    this.hasMorePages = true;
    this.isLoading = false;
  }
  
  /**
   * クリーンアップ
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }
  
  /**
   * 商品を在庫状況に基づいてソート
   */
  sortProducts() {
    const startTime = performance.now();
    
    // すでにソート中の場合は何もしない（無限ループ防止）
    if (this.isSorting) {
      this.log('[ソート] スキップ（既にソート中）');
      return;
    }
    
    const productItems = Array.from(this.container.querySelectorAll('[data-product-grid-item]'));
    
    if (productItems.length === 0) {
      this.log('[ソート] スキップ（商品なし）');
      return;
    }
    
    this.log(`[ソート] 開始 - ${productItems.length}商品`);
    this.isSorting = true;
    
    // MutationObserverを一時停止
    if (this.observer) {
      this.observer.disconnect();
      this.log('[ソート] MutationObserver停止');
    }
    
    // 商品を2つのカテゴリーに分類
    const classifyStart = performance.now();
    const frontProducts = []; // 在庫あり + 在庫切れでも販売継続
    const backProducts = [];  // 在庫切れで販売停止
    
    productItems.forEach(item => {
      const productInfo = this.getProductInfo(item);
      
      if (productInfo.isInStock || productInfo.continueSellingWhenOutOfStock) {
        frontProducts.push(item);
      } else {
        backProducts.push(item);
      }
    });
    const classifyEnd = performance.now();
    this.log(`[ソート] 分類完了（${(classifyEnd - classifyStart).toFixed(2)}ms）- 前:${frontProducts.length} 後:${backProducts.length}`);
    
    // ソート後の順序: 在庫あり・在庫切れでも販売継続 → 在庫切れ
    const sortedProducts = [...frontProducts, ...backProducts];
    
    // DOMに再配置
    const rearrangeStart = performance.now();
    const loadingElement = this.container.querySelector('.product-grid__loader');
    const sentinel = this.container.querySelector('.infinite-scroll-sentinel');
    const insertBefore = sentinel || loadingElement;
    
    sortedProducts.forEach(item => {
      if (insertBefore) {
        this.container.insertBefore(item, insertBefore);
      } else {
        this.container.appendChild(item);
      }
    });
    const rearrangeEnd = performance.now();
    this.log(`[ソート] DOM再配置完了（${(rearrangeEnd - rearrangeStart).toFixed(2)}ms）`);
    
    const totalTime = performance.now() - startTime;
    this.log(`[ソート] 全体完了（${totalTime.toFixed(2)}ms）`);
    
    // ソート完了後、少し待ってからObserverを再開
    setTimeout(() => {
      this.isSorting = false;
      
      // MutationObserverを再開
      if (this.observer) {
        this.observer.observe(this.container, {
          childList: true,
          subtree: false
        });
        this.log('[ソート] MutationObserver再開');
      }
    }, 200);
  }
  
  /**
   * 商品要素から在庫情報を取得
   */
  getProductInfo(productElement) {
    // data属性から在庫情報を取得
    const isAvailable = productElement.dataset.productAvailable === 'true';
    const continueSellingEnabled = productElement.dataset.productContinueSelling === 'true';
    
    // 商品URLから商品ハンドルを取得
    const productLink = productElement.querySelector('[data-grid-link]');
    let productHandle = '';
    if (productLink) {
      const href = productLink.getAttribute('href');
      const matches = href.match(/\/products\/([^?]+)/);
      if (matches) {
        productHandle = matches[1];
      }
    }
    
    return {
      isInStock: isAvailable,
      continueSellingWhenOutOfStock: continueSellingEnabled,
      productHandle: productHandle
    };
  }
  
  /**
   * 無限スクロールのセットアップ
   */
  setupInfiniteScroll() {
    const createObserver = () => {
      // 既存のsentinelがあれば削除
      const existingSentinel = this.container.querySelector('.infinite-scroll-sentinel');
      if (existingSentinel) {
        existingSentinel.remove();
      }
      
      // Sentinelを作成
      const sentinel = document.createElement('div');
      sentinel.className = 'infinite-scroll-sentinel';
      sentinel.style.height = '10px';
      sentinel.style.width = '100%';
      sentinel.style.clear = 'both';
      sentinel.style.display = 'block';
      
      // Sentinelを商品リストの最後、loaderの前に配置
      const loadingElement = this.container.querySelector('.product-grid__loader');
      if (loadingElement) {
        this.container.insertBefore(sentinel, loadingElement);
      } else {
        this.container.appendChild(sentinel);
      }
      
      // IntersectionObserverの設定
      const observerOptions = {
        root: null,
        rootMargin: '600px', // バランスの取れた先読み距離
        threshold: [0, 0.5, 1]
      };
      
      this.log('[IntersectionObserver] 設定完了 - rootMargin: 600px');
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.log(`[IntersectionObserver] トリガー - isIntersecting: ${entry.isIntersecting}, ratio: ${entry.intersectionRatio.toFixed(2)}`);
          
          // 初期化完了、読み込み中でない、まだページがある、実際に交差している
          if (this.isInitialized && entry.isIntersecting && !this.isLoading && this.hasMorePages && entry.intersectionRatio > 0) {
            this.log('[IntersectionObserver] 条件満たす → loadMoreProducts実行');
            this.loadMoreProducts();
          } else {
            const reasons = [];
            if (!this.isInitialized) reasons.push('未初期化');
            if (!entry.isIntersecting) reasons.push('未交差');
            if (this.isLoading) reasons.push('読み込み中');
            if (!this.hasMorePages) reasons.push('ページなし');
            if (entry.intersectionRatio <= 0) reasons.push('ratio≤0');
            this.log(`[IntersectionObserver] スキップ - 理由: ${reasons.join(', ')}`);
          }
        });
      }, observerOptions);
      
      observer.observe(sentinel);
      
      // Observerを保存
      this.intersectionObserver = observer;
      
      // 高速スクロール対応：スクロール終了時に手動チェック
      let scrollTimeout;
      const checkSentinelPosition = () => {
        if (!this.isInitialized || this.isLoading || !this.hasMorePages) {
          this.log('[スクロール検知] スキップ（初期化前/読み込み中/ページなし）');
          return;
        }
        
        const currentSentinel = this.container.querySelector('.infinite-scroll-sentinel');
        if (!currentSentinel) {
          this.log('[スクロール検知] Sentinel要素が見つかりません');
          return;
        }
        
        const rect = currentSentinel.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const distance = rect.top - viewportHeight;
        
        this.log(`[スクロール検知] Sentinel位置: ${distance.toFixed(0)}px（画面下から）`);
        
        // Sentinelが画面内または画面下から600px以内にある場合
        if (rect.top < viewportHeight + 600) {
          this.log('[スクロール検知] 範囲内 → loadMoreProducts実行');
          this.loadMoreProducts();
        } else {
          this.log('[スクロール検知] 範囲外（600px以上離れている）');
        }
      };
      
      const handleScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.log('[スクロール] 終了検知 → 位置チェック開始');
          checkSentinelPosition();
        }, 150); // スクロール終了150ms後にチェック（応答性向上）
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      this.log('[スクロール] イベントリスナー登録完了');
      
      // クリーンアップ用に保存
      this.scrollHandler = handleScroll;
    };
    
    createObserver();
  }
  
  /**
   * 次のページの商品を読み込む
   */
  async loadMoreProducts() {
    if (this.isLoading || !this.hasMorePages) {
      this.log('[読み込み] スキップ（読み込み中またはページなし）');
      return;
    }
    
    const startTime = performance.now();
    this.log(`[読み込み] 開始 - ページ${this.currentPage + 1}を読み込み中`);
    
    this.isLoading = true;
    this.showLoader();
    
    try {
      this.currentPage++;
      
      // URLパラメータを構築
      const url = new URL(this.collectionUrl, window.location.origin);
      url.searchParams.set('page', this.currentPage);
      
      // 現在のソートパラメータを保持
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has('sort_by')) {
        url.searchParams.set('sort_by', currentParams.get('sort_by'));
      }
      
      // フィルターパラメータを保持
      for (const [key, value] of currentParams.entries()) {
        if (key.startsWith('filter.')) {
          url.searchParams.set(key, value);
        }
      }
      
      const fetchStart = performance.now();
      const response = await fetch(url.toString());
      const html = await response.text();
      const fetchEnd = performance.now();
      this.log(`[読み込み] fetch完了（${(fetchEnd - fetchStart).toFixed(2)}ms）`);
      
      // レスポンスからHTMLをパース
      const parseStart = performance.now();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // 新しい商品を取得
      const newProducts = doc.querySelectorAll('[data-products-grid] [data-product-grid-item]');
      const parseEnd = performance.now();
      this.log(`[読み込み] パース完了（${(parseEnd - parseStart).toFixed(2)}ms）- ${newProducts.length}商品`);
      
      if (newProducts.length === 0) {
        this.hasMorePages = false;
        this.log('[読み込み] これ以上商品なし');
      } else {
        // 新しい商品を追加
        const insertStart = performance.now();
        const sentinel = this.container.querySelector('.infinite-scroll-sentinel');
        
        newProducts.forEach(product => {
          const clonedProduct = product.cloneNode(true);
          this.container.insertBefore(clonedProduct, sentinel);
        });
        const insertEnd = performance.now();
        this.log(`[読み込み] DOM挿入完了（${(insertEnd - insertStart).toFixed(2)}ms）`);
        
        // 追加後に全体をソート
        const sortStart = performance.now();
        this.sortProducts();
        const sortEnd = performance.now();
        this.log(`[読み込み] ソート完了（${(sortEnd - sortStart).toFixed(2)}ms）`);
        
        // 遅延読み込み画像を初期化（メインスレッドの負荷を分散）
        requestAnimationFrame(() => {
          const imageStart = performance.now();
          if (window.lazySizes) {
            const lazyImages = this.container.querySelectorAll('.lazyload:not(.lazyloaded)');
            this.log(`[読み込み] lazySizes初期化 - ${lazyImages.length}画像`);
            lazyImages.forEach(img => {
              window.lazySizes.loader.unveil(img);
            });
          }
          
          // AOS（アニメーション）を再初期化
          if (window.AOS) {
            window.AOS.refresh();
            this.log('[読み込み] AOS再初期化完了');
          }
          const imageEnd = performance.now();
          this.log(`[読み込み] 画像/アニメーション初期化完了（${(imageEnd - imageStart).toFixed(2)}ms）`);
        });
      }
      
      const endTime = performance.now();
      this.log(`[読み込み] 全体完了（${(endTime - startTime).toFixed(2)}ms）`);
      
    } catch (error) {
      console.error('[CollectionInfiniteScroll] 商品の読み込みに失敗しました:', error);
      this.hasMorePages = false;
    } finally {
      this.isLoading = false;
      this.hideLoader();
    }
  }
  
  /**
   * ローディング表示
   */
  showLoader() {
    const loader = this.container.querySelector('.product-grid__loader');
    if (loader) {
      loader.classList.add('is-loading');
    }
  }
  
  /**
   * ローディング非表示
   */
  hideLoader() {
    const loader = this.container.querySelector('.product-grid__loader');
    if (loader) {
      loader.classList.remove('is-loading');
    }
  }
}

// DOM読み込み完了後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CollectionInfiniteScroll();
  });
} else {
  new CollectionInfiniteScroll();
}

// テーマエディタでのセクション再読み込みに対応
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-section-type="collection"]')) {
    new CollectionInfiniteScroll();
  }
});

